import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ViolationItem from './ViolationItem'
import ContactForm from './ContactForm'
import DomainScopePicker from './DomainScopePicker'
import { decomposeDomainScope, depthsForName, hasFixedScope } from './domainScope'
import './OrganizationSection.css'

class OrganizationSection extends Component {
	// Which domains the user took off the scope picker to type the name by hand,
	// keyed by domain id.
	state = { manualDigitalPropertyIds: {} }

	setManual = (digitalPropertyId, manual) => {
		this.setState((state) => ({
			manualDigitalPropertyIds: { ...state.manualDigitalPropertyIds, [digitalPropertyId]: manual },
		}))
	}

	// The picker needs a case URL to decompose, so a domain added by hand — with
	// no violation behind it yet — is the one place a text field stands in for
	// it. Everything else picks, unless the user asked to type.
	// Instagram and Facebook scopes come from determine-digital-property and are
	// shown as they are: there is nothing here to pick apart, and the one scope
	// the picker could reach past the resolved name is the bare platform domain.
	isFixedScope = (domain) => hasFixedScope(domain.sourceUrl || domain.name)

	scopeFor = (domain) => {
		if (this.isFixedScope(domain)) return null
		if (!domain.sourceUrl) return null
		const decomposition = decomposeDomainScope({
			name: domain.name,
			sourceUrl: domain.sourceUrl,
			baselineDomain: domain.baselineDomain,
			lockBaselinePath: domain.lockBaselinePath,
		})
		if (!decomposition) return null
		// The decomposition is built around the name, so it maps back unless the
		// name was emptied in manual mode — then offer the whole URL again.
		const depths = depthsForName(decomposition, domain.name) || {
			subDepth: decomposition.subdomainLabels.length,
			pathDepth: decomposition.pathSegments.length,
		}
		return { decomposition, ...depths }
	}

	render() {
		const {
			org,
			index,
			canDelete,
			onDomainNameChange,
			onAddDomain,
			onDeleteDomain,
			onDelete,
			onFieldChange,
			onArrayFieldChange,
			onAddArrayField,
			onRemoveArrayField,
			onPhoneFieldChange,
			onValidationChange,
			onFormValidChange,
			onOrganizationFound,
			onSaveModeChange,
			onAddOrganizationFromViolation,
		} = this.props

		const isOriginal = !!org.isOriginal

		// What this card does on save.
		//
		// "Update the existing organization" is hidden for now — writing back to
		// the organization the modal was opened on does not match how these cases
		// are worked today. The mode itself is still implemented end to end
		// (OrganizationSplitModal.organizationIdForSave), so restoring it is a
		// matter of uncommenting this entry and letting the original card default
		// to it again.
		//
		// const canUpdate = isOriginal && !!org.organizationId
		const saveModes = [
			// ...(canUpdate
			// 	? [
			// 			{
			// 				value: 'update',
			// 				label: org.loadedCompanyName
			// 					? `Update "${org.loadedCompanyName}"`
			// 					: 'Update the existing organization',
			// 			},
			// 		]
			// 	: []),
			{ value: 'create', label: 'Create a new organization' },
			{ value: 'link', label: 'Link to existing org by org. no.' },
		]

		return (
			<div className="subdomain-section">
				{/* Organization Header */}
				<div className="subdomain-header">
					<div className="subdomain-number" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						Organization {index + 1}
						{org.isOriginal && (
							<span style={{
								background: '#3b82f6',
								color: 'white',
								fontSize: '10px',
								fontWeight: '600',
								padding: '2px 6px',
								borderRadius: '4px',
							}}>
								Original
							</span>
						)}
					</div>
					{canDelete && !isOriginal && (
						<button
							className="delete-subdomain-button"
							onClick={onDelete}
							title="Delete organization"
						>
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
					)}
				</div>

				{/* What happens to this card on save */}
				<div className="save-mode-section">
					<div className="save-mode-label">On save</div>
					{saveModes.map((mode) => (
						<label key={mode.value} className="save-mode-option">
							<input
								type="radio"
								name={`save-mode-${org.id}`}
								value={mode.value}
								checked={org.saveMode === mode.value}
								onChange={() => onSaveModeChange(mode.value)}
							/>
							<span>{mode.label}</span>
						</label>
					))}
					{org.saveMode === 'link' && !org.linkedOrganizationId && (
						<div className="save-mode-hint">
							Enter an organisation number below and pick the match.
						</div>
					)}
				</div>

				{/* Domains within this organization */}
				{org.domains.map((domain, domainIndex) => {
					const scope = this.scopeFor(domain)
					const fixedScope = this.isFixedScope(domain)
					// Picking is the default; only the toggle takes it away.
					const usePicker = !!scope && !this.state.manualDigitalPropertyIds[domain.id]

					return (
					<div key={domain.id} style={{ marginBottom: '16px' }}>
						<div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginBottom: '8px' }}>
							<div style={{ flex: 1 }}>
								<div className="subdomain-label-row">
									<label className="subdomain-label">Domain {domainIndex + 1} *</label>
									{scope && !fixedScope && (
										<button
											type="button"
											className="domain-scope-toggle"
											onClick={() => this.setManual(domain.id, usePicker)}
										>
											{usePicker ? 'Enter manually' : 'Use picker'}
										</button>
									)}
								</div>
								{fixedScope ? (
									<div className="subdomain-input subdomain-input-fixed">
										{domain.name}
									</div>
								) : usePicker ? (
									<DomainScopePicker
										decomposition={scope.decomposition}
										subDepth={scope.subDepth}
										pathDepth={scope.pathDepth}
										hasError={!!domain.nameValidationError}
										onChange={(name) => onDomainNameChange(domain.id, name)}
									/>
								) : (
									<input
										type="text"
										className={`subdomain-input ${domain.nameValidationError ? 'error' : ''}`}
										value={domain.name}
										onChange={(e) => onDomainNameChange(domain.id, e.target.value)}
										placeholder="e.g., example.com"
									/>
								)}
								{fixedScope && (
									<div className="subdomain-note">
										Domain scope is fixed for Instagram and Facebook: it is
										resolved from the case URL.
									</div>
								)}
								{domain.loadedName && domain.name !== domain.loadedName && (
									<div className="subdomain-note">
										The cases below move to this domain. "{domain.loadedName}"
										stays attached to this organization, along with any cases
										still on it.
									</div>
								)}
								{domain.nameValidationError && (
									<div className="subdomain-error">{domain.nameValidationError}</div>
								)}
							</div>
							{org.domains.length > 1 && !isOriginal && (
								<button
									className="delete-subdomain-button"
									onClick={() => onDeleteDomain(domain.id)}
									title="Remove domain"
									style={{ marginBottom: '4px' }}
								>
									<svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
										<path
											fillRule="evenodd"
											d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
											clipRule="evenodd"
										/>
									</svg>
								</button>
							)}
						</div>

						{/* Violations for this domain */}
						<div className="violations-section">
							<h4 className="section-title">
								Violations ({domain.violations.length})
							</h4>
							{domain.violations.length === 0 ? (
								<div className="no-violations">
									No violations assigned. Edit the domain name to auto-assign
									violations based on URL matching.
									{org.saveMode === 'update' &&
										' This organization can still be saved with no violations — that is what splitting a case out to a branch leaves behind.'}
								</div>
							) : (
								<div className="violations-list">
									{domain.violations.map((violation) => (
										<ViolationItem
										key={violation.violationId}
										violation={violation}
										onAddOrganization={onAddOrganizationFromViolation}
									/>
									))}
								</div>
							)}
						</div>
					</div>
					)
				})}

				{/*<button*/}
				{/*	type="button"*/}
				{/*	onClick={onAddDomain}*/}
				{/*	style={{*/}
				{/*		background: '#e5e7eb',*/}
				{/*		border: 'none',*/}
				{/*		borderRadius: '4px',*/}
				{/*		padding: '6px 12px',*/}
				{/*		fontSize: '12px',*/}
				{/*		fontWeight: '600',*/}
				{/*		cursor: 'pointer',*/}
				{/*		marginBottom: '16px',*/}
				{/*	}}*/}
				{/*>*/}
				{/*	+ Add Domain*/}
				{/*</button>*/}

				{/* Contact Information */}
				<div className="contact-section">
					<h4 className="section-title">Contact Information</h4>
					<ContactForm
						// ContactForm reads organizationId only to decide between the
						// "Link to existing org" button and the linked confirmation, so
						// show it the organization this card will actually write to.
						data={{
							...org,
							organizationId:
								org.saveMode === 'update'
									? org.organizationId
									: org.linkedOrganizationId,
						}}
						// The card offers a choice of which organization to write to,
						// so a number belonging to another one is a blocking conflict.
						blockOnCompanyNumberConflict
						onFieldChange={onFieldChange}
						onArrayFieldChange={onArrayFieldChange}
						onAddArrayField={onAddArrayField}
						onRemoveArrayField={onRemoveArrayField}
						onPhoneFieldChange={onPhoneFieldChange}
						onValidationChange={onValidationChange}
						onFormValidChange={onFormValidChange}
						onOrganizationFound={onOrganizationFound}
					/>
				</div>
			</div>
		)
	}
}

OrganizationSection.propTypes = {
	org: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
	canDelete: PropTypes.bool.isRequired,
	onDomainNameChange: PropTypes.func.isRequired,
	onAddDomain: PropTypes.func.isRequired,
	onDeleteDomain: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onFieldChange: PropTypes.func.isRequired,
	onArrayFieldChange: PropTypes.func.isRequired,
	onAddArrayField: PropTypes.func.isRequired,
	onRemoveArrayField: PropTypes.func.isRequired,
	onPhoneFieldChange: PropTypes.func.isRequired,
	onValidationChange: PropTypes.func,
	onFormValidChange: PropTypes.func,
	onOrganizationFound: PropTypes.func,
	onSaveModeChange: PropTypes.func.isRequired,
	onAddOrganizationFromViolation: PropTypes.func,
}

export default OrganizationSection
